import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calculator, Copy, Plus, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import type { PricingTemplate } from "../lib/types";
import { dateTime, money } from "../lib/format";
import { Modal } from "../components/Modal";
import { useI18n } from "../lib/i18n";

const defaultForm = {
  name: "",
  site: "global",
  type: "cross_border",
  currency: "USD",
  profitMode: "sale_profit_rate",
  profitRate: "20",
  fixedProfit: "0",
  discountRate: "5",
  shippingMode: "fixed",
  shippingFee: "0",
  commissionRate: "5",
  creatorCommissionRate: "0",
  otherFee: "0",
  exchangeRate: "1",
  rounding: "none",
  active: true
};

export function PricingTemplatesPage() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PricingTemplate | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [calculator, setCalculator] = useState({ cost: "100", weight: "0.3" });
  const query = useQuery({
    queryKey: ["pricing-templates"],
    queryFn: () => api.get<{ templates: PricingTemplate[] }>("/api/catalog/pricing-templates")
  });
  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch(`/api/catalog/pricing-templates/${editing.id}`, form)
        : api.post("/api/catalog/pricing-templates", form),
    onSuccess: async () => {
      setOpen(false);
      setEditing(null);
      setForm(defaultForm);
      await queryClient.invalidateQueries({ queryKey: ["pricing-templates"] });
    }
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/catalog/pricing-templates/${id}`),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["pricing-templates"] })
  });

  function openEditor(template?: PricingTemplate) {
    setEditing(template ?? null);
    setForm(
      template
        ? {
            name: template.name,
            site: template.site,
            type: template.type,
            currency: template.currency,
            profitMode: template.profit_mode,
            profitRate: String(template.profit_rate),
            fixedProfit: String(template.fixed_profit),
            discountRate: String(template.discount_rate),
            shippingMode: template.shipping_mode,
            shippingFee: String(template.shipping_fee),
            commissionRate: String(template.commission_rate),
            creatorCommissionRate: String(template.creator_commission_rate),
            otherFee: String(template.other_fee),
            exchangeRate: String(template.exchange_rate),
            rounding: template.rounding,
            active: template.active === 1
          }
        : defaultForm
    );
    setOpen(true);
  }

  const preview = (() => {
    const cost = Number(calculator.cost || 0);
    const weight = Math.max(0.01, Number(calculator.weight || 0));
    const profitRate = Number(form.profitRate || 0);
    const commission = Number(form.commissionRate || 0) + Number(form.creatorCommissionRate || 0);
    const shipping = form.shippingMode === "weight" ? Number(form.shippingFee || 0) * weight : Number(form.shippingFee || 0);
    const denominator = Math.max(0.01, 1 - (profitRate + commission) / 100);
    const sale = (cost + shipping + Number(form.otherFee || 0) + Number(form.fixedProfit || 0)) / denominator;
    const list = sale / (Math.max(1, Number(form.discountRate || 10)) / 10);
    return { sale, list, profit: sale - cost - shipping - Number(form.otherFee || 0) };
  })();

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <div>
          <span className="muted">{t("定价模板把成本、运费、平台佣金、达人佣金、利润率和折扣组合成可复用规则。")}</span>
        </div>
        <button type="button" className="primary-button" onClick={() => openEditor()}>
          <Plus size={16} />
          {t("创建模板")}
        </button>
      </div>
      <div className="pricing-template-grid">
        {(query.data?.templates ?? []).map((template) => (
          <section className="panel pricing-template-card" key={template.id}>
            <div className="pricing-template-head">
              <div>
                <h2>{template.name}</h2>
                <span>{template.site} · {template.type}</span>
              </div>
              <span className="template-rate">{template.profit_rate}%</span>
            </div>
            <div className="template-metrics">
              <span>{t("运费")} <strong>{money(template.shipping_fee, template.currency)}</strong></span>
              <span>{t("平台佣金")} <strong>{template.commission_rate}%</strong></span>
              <span>{t("折扣")} <strong>{template.discount_rate} {t("折")}</strong></span>
              <span>{t("汇率")} <strong>{template.exchange_rate}</strong></span>
            </div>
            <div className="template-foot">
              <small>{t("更新于")} {dateTime(template.updated_at)}</small>
              <div className="button-row">
                <button type="button" className="icon-button" onClick={() => openEditor(template)} aria-label={t("编辑")}>
                  <Copy size={15} />
                </button>
                <button type="button" className="icon-button danger-button" onClick={() => remove.mutate(template.id)} aria-label={t("删除")}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </section>
        ))}
      </div>

      <Modal open={open} title={editing ? t("编辑定价模板") : t("创建定价模板")} onClose={() => setOpen(false)}>
        <div className="pricing-editor">
          <div className="pricing-editor-form">
            <div className="form-grid">
              <label className="field span-2">
                <span>{t("模板名称")}</span>
                <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("站点")}</span>
                <input value={form.site} onChange={(event) => setForm({ ...form, site: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("币种")}</span>
                <input maxLength={3} value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })} />
              </label>
              <label className="field">
                <span>{t("利润方式")}</span>
                <select value={form.profitMode} onChange={(event) => setForm({ ...form, profitMode: event.target.value })}>
                  <option value="sale_profit_rate">{t("售价利润率")}</option>
                  <option value="cost_profit_rate">{t("成本利润率")}</option>
                  <option value="fixed_profit">{t("固定利润")}</option>
                </select>
              </label>
              <label className="field">
                <span>{t("利润率 %")}</span>
                <input type="number" value={form.profitRate} onChange={(event) => setForm({ ...form, profitRate: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("固定利润")}</span>
                <input type="number" value={form.fixedProfit} onChange={(event) => setForm({ ...form, fixedProfit: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("折扣")}</span>
                <input type="number" step="0.1" value={form.discountRate} onChange={(event) => setForm({ ...form, discountRate: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("运费方式")}</span>
                <select value={form.shippingMode} onChange={(event) => setForm({ ...form, shippingMode: event.target.value })}>
                  <option value="fixed">{t("统一运费")}</option>
                  <option value="weight">{t("按重量计费")}</option>
                </select>
              </label>
              <label className="field">
                <span>{t("运费")}</span>
                <input type="number" value={form.shippingFee} onChange={(event) => setForm({ ...form, shippingFee: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("平台佣金 %")}</span>
                <input type="number" value={form.commissionRate} onChange={(event) => setForm({ ...form, commissionRate: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("达人佣金 %")}</span>
                <input type="number" value={form.creatorCommissionRate} onChange={(event) => setForm({ ...form, creatorCommissionRate: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("其他费用")}</span>
                <input type="number" value={form.otherFee} onChange={(event) => setForm({ ...form, otherFee: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("汇率")}</span>
                <input type="number" step="0.0001" value={form.exchangeRate} onChange={(event) => setForm({ ...form, exchangeRate: event.target.value })} />
              </label>
              <label className="field">
                <span>{t("取整方式")}</span>
                <select value={form.rounding} onChange={(event) => setForm({ ...form, rounding: event.target.value })}>
                  <option value="none">{t("不取整")}</option>
                  <option value="up">{t("向上取整")}</option>
                  <option value="down">{t("向下取整")}</option>
                  <option value="nearest_99">{t("取整到 .99")}</option>
                </select>
              </label>
            </div>
          </div>
          <aside className="pricing-calculator">
            <div className="calculator-head">
              <Calculator size={18} />
              <strong>{t("售价试算")}</strong>
            </div>
            <label className="field"><span>{t("采购成本")}</span><input type="number" value={calculator.cost} onChange={(event) => setCalculator({ ...calculator, cost: event.target.value })} /></label>
            <label className="field"><span>{t("重量 KG")}</span><input type="number" step="0.01" value={calculator.weight} onChange={(event) => setCalculator({ ...calculator, weight: event.target.value })} /></label>
            <div className="calculator-result">
              <span>{t("折后售价")}<strong>{money(preview.sale, form.currency)}</strong></span>
              <span>{t("折前售价")}<strong>{money(preview.list, form.currency)}</strong></span>
              <span>{t("预估利润")}<strong>{money(preview.profit, form.currency)}</strong></span>
            </div>
          </aside>
        </div>
        {save.isError ? <div className="form-error">{save.error.message}</div> : null}
        <div className="modal-actions">
          <button type="button" className="ghost-button" onClick={() => setOpen(false)}>{t("取消")}</button>
          <button type="button" className="primary-button" disabled={!form.name || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t("保存中...") : t("保存模板")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
